import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import reklamacjeService from '../services/reklamacjeService';

/**
 * Podgląd / druk / pobranie PDF protokołu reklamacji.
 *
 * Wzorzec 1:1 z CorrectionPrintPage.jsx / ReturnPrintPage.jsx: pobiera PDF jako
 * blob przez autoryzowany request (reklamacjeService.downloadPdf -> instancja `api`
 * z location_id/CSRF/device tokenem), buduje objectURL i renderuje w <iframe>.
 * Trasa: /reklamacje/:id/pdf-podglad (App.jsx).
 *
 * UWAGA: objectURL jest zwalniany przy odmontowaniu / zmianie id (revokeObjectURL),
 * żeby nie wyciekać pamięci blobów PDF.
 */
const ReklamacjaPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let objectUrl = null;
    let anulowane = false;

    const loadReklamacjaPdf = async () => {
      setLoading(true);
      setError('');
      try {
        // Blob PDF (responseType:'blob') — inline do podglądu w iframe
        const blob = await reklamacjeService.downloadPdf(id);
        if (anulowane) return;
        // Upewnij się, że mamy poprawny typ (backend zwraca application/pdf)
        const pdfBlob =
          blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfUrl(objectUrl);
      } catch (err) {
        if (anulowane) return;
        console.error('Błąd pobierania PDF reklamacji:', err);
        setError(err?.message || 'Błąd pobierania PDF reklamacji');
      } finally {
        if (!anulowane) setLoading(false);
      }
    };

    loadReklamacjaPdf();

    return () => {
      anulowane = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const handleDownload = async () => {
    try {
      // Osobny blob z nagłówkiem attachment (download=1) na potrzeby zapisu na dysk
      const blob = await reklamacjeService.downloadPdf(id, true);
      const pdfBlob =
        blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reklamacja_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Zwolnij po chwili, żeby zdążyło pobrać
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      console.error('Błąd pobierania PDF reklamacji:', err);
      setError(err?.message || 'Błąd pobierania PDF reklamacji');
    }
  };

  const handleClose = () => {
    // W samodzielnym oknie (window.open) zamknij; inaczej wróć do listy reklamacji
    if (window.opener) {
      window.close();
    } else {
      navigate('/reklamacje');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid var(--info)',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Generowanie protokołu reklamacji PDF...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        background: 'var(--bg)',
        minHeight: '100vh'
      }}>
        <div style={{
          background: 'var(--card-bg)',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          margin: '0 auto'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h3 style={{ color: 'var(--danger)', margin: '0 0 10px' }}>Błąd</h3>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 20px' }}>{error}</p>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 30px',
              border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg)'
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
        padding: '15px',
        background: 'var(--card-bg)',
        borderBottom: '1px solid var(--border)',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={handlePrint}
          style={{
            padding: '12px 30px',
            border: '1px solid var(--danger)', color: 'var(--danger)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          🖨️ Drukuj reklamację
        </button>
        <button
          onClick={handleDownload}
          style={{
            padding: '12px 30px',
            border: '1px solid var(--info)', color: 'var(--info)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          📥 Pobierz PDF
        </button>
        <button
          onClick={handleClose}
          style={{
            padding: '12px 30px',
            border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ✖️ Zamknij
        </button>
      </div>

      {/* PDF Viewer */}
      <div style={{ flex: 1, padding: '20px', overflow: 'hidden' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
            title="Podgląd protokołu reklamacji"
          />
        )}
      </div>
    </div>
  );
};

export default ReklamacjaPrintPage;
